/* global window */

import * as firebase from 'firebase'
import moment from 'moment'
import MobileDetect from 'mobile-detect'

import Debouncer from '../infra/Debouncer'

const FunctionUtils = {

  isUserOnTablet() {
    var md = new MobileDetect(window.navigator.userAgent);
    return md.tablet() || md.mobile();
  },

  removeWorker(teamId, jobInfo, workerId) {
    FunctionUtils.updateModule(jobInfo, '/modules/workers/list/' + workerId, null);

    if (!jobInfo.temporal) {
      const firebaseUpdates = {};
      const calendarId = `${workerId}@${teamId}`;

      let cursor = moment(jobInfo.modules.date.startDate);
      while (cursor.isSameOrBefore(jobInfo.modules.date.endDate)) {
        const dateStr = cursor.format('YYYY-MM-DD');
        if (!jobInfo.modules.date.recurrence ||
          FunctionUtils.isInsideRecurrence(dateStr, jobInfo.modules.date)) {
          const instancePath = `calendars/${calendarId}/instances/${dateStr}/${jobInfo.id}`;
          firebaseUpdates[instancePath] = null;
        }
        cursor.add(1, 'days');
      }

      firebase.database().ref().update(firebaseUpdates);
    }
  },

  selectWorker(teamId, jobInfo, workerId) {
    FunctionUtils.updateModule(jobInfo, '/modules/workers/list/' + workerId, {id: workerId});

    if (!jobInfo.temporal) {
      const addWorkerFunction = (deletedDateStrs) => {
        const firebaseUpdates = {};
        const calendarId = `${workerId}@${teamId}`;

        let cursor = moment(jobInfo.modules.date.startDate);
        while (cursor.isSameOrBefore(jobInfo.modules.date.endDate)) {
          const dateStr = cursor.format('YYYY-MM-DD');

          if (!deletedDateStrs.hasOwnProperty(dateStr)) {
            if (!jobInfo.modules.date.recurrence ||
              FunctionUtils.isInsideRecurrence(dateStr, jobInfo.modules.date)) {
              const instancePath = `calendars/${calendarId}/instances/${dateStr}/${jobInfo.id}`;
              firebaseUpdates[instancePath] = {
                dateStr: dateStr,
                eventId: jobInfo.id,
                calendarId: calendarId
              };
            }
          }

          cursor.add(1, 'days');
        }

        firebase.database().ref().update(firebaseUpdates);
      }

      if (jobInfo.modules.date.recurrence) {
        firebase.database()
          .ref(`events/${jobInfo.id}/extendedInfo/modules/date/deletedInstances/`)
          .once('value', (snapshot) => {
            const val = snapshot.val();
            addWorkerFunction(val ? val : {})
          })
      } else {
        addWorkerFunction({});
      }
    }
  },

  updateModule(jobInfo, modulePath, value, debounce) {
    const updates = {};
    updates[`events/${jobInfo.id}/commonInfo/` + modulePath] = value;

    if (debounce){
      // Save in db debounced with 3 secs
      const delay = 1000;
      const uniqueId = `events/${jobInfo.id}/commonInfo/${modulePath}`;
      Debouncer.debounce(
        () => firebase.database().ref().update(updates),
        delay,
        uniqueId
      );
    } else {
      firebase.database().ref().update(updates);
    }


  },

  deleteJob(teamId, jobInfo) {
    const path = `events/${jobInfo.id}/`;
    const firebaseUpdates = {};

    firebaseUpdates[path] = null;

    // Remove instances
    if (jobInfo.modules.workers.list) {
      // Create instances
      for (const workerId in jobInfo.modules.workers.list) {
        const calendarId = `${workerId}@${teamId}`;

        let cursor = moment(jobInfo.modules.date.startDate);
        while (cursor.isSameOrBefore(jobInfo.modules.date.endDate)) {
          const dateStr = cursor.format('YYYY-MM-DD');
          if (!jobInfo.modules.date.recurrence ||
            FunctionUtils.isInsideRecurrence(dateStr, jobInfo.modules.date)) {
            const instancePath = `calendars/${calendarId}/instances/${dateStr}/${jobInfo.id}`;
            firebaseUpdates[instancePath] = null;
          }
          cursor.add(1, 'days');
        }
      }
    }

    firebase.database().ref().update(firebaseUpdates);
  },

  // Create a new job
  createJob(teamId, jobInfo) {
    const path = `events/${jobInfo.id}/`;
    const firebaseUpdates = {};

    // Shared tasks
    if (jobInfo.modules.tasks && jobInfo.modules.tasks._type === 'shared') {
      firebaseUpdates[path + 'extendedInfo/modules/tasks'] = jobInfo.modules.tasks;
      delete jobInfo.modules.tasks;
      jobInfo.modules.sharedTasks = {_type: 'shared'};
    }

    // Shared notesTeam
    if (jobInfo.modules.notesTeam && jobInfo.modules.notesTeam._type === 'shared') {
      firebaseUpdates[path + 'extendedInfo/modules/notesTeam'] = jobInfo.modules.notesTeam;
      delete jobInfo.modules.notesTeam;
      jobInfo.modules.sharedNotesTeam = {_type: 'shared'};
    }

    // Adding teamId
    if (!jobInfo.teamId){
      jobInfo.teamId = teamId;
    }

    // Add instances
    delete jobInfo.temporal;
    if (jobInfo.modules.workers.list) {
      // Create instances
      for (const workerId in jobInfo.modules.workers.list) {
        const calendarId = `${workerId}@${teamId}`;

        let cursor = moment(jobInfo.modules.date.startDate);
        while (cursor.isSameOrBefore(jobInfo.modules.date.endDate)) {
          const dateStr = cursor.format('YYYY-MM-DD');
          if (!jobInfo.modules.date.recurrence ||
            FunctionUtils.isInsideRecurrence(dateStr, jobInfo.modules.date)) {
            const instancePath = `calendars/${calendarId}/instances/${dateStr}/${jobInfo.id}`;
            firebaseUpdates[instancePath] = {
              dateStr: dateStr,
              eventId: jobInfo.id,
              calendarId: calendarId
            };
          }
          cursor.add(1, 'days');
        }
      }
    }

    firebase.database().ref().update(firebaseUpdates);
    firebase.database().ref(path + 'commonInfo/').set(jobInfo);
  },

  getJobType : (jobInfo) => {
    const { date, lineItems } = jobInfo.modules
    if (date.recurrence){
      return 'recurrent'
    } else if (lineItems) {
      return 'install'
    } else {
      return 'service'
    }
  },

  //TOOLS
  cloneOrDefault(source) {
    return !source ? {} : JSON.parse(JSON.stringify(source));
  },

  isInsideRecurrence(day, dateModule) {
    const recurrence = dateModule.recurrence;
    const startDate = moment(dateModule.startDate);

    if (recurrence.repeats === 'weekly') {
      // Right week?
      const weeks = moment(day).startOf('week').diff(moment(startDate).startOf('week'), 'weeks');
      if (weeks % recurrence.repeatOcurrency !== 0) {
        return false;
      }

      // Right weekday?
      const weekday = moment(day).isoWeekday()
      return recurrence.repeatOn[weekday] === true;
    }

    if (recurrence.repeats === 'monthly') {
      // Right Month?
      const months = moment(day).startOf('month').diff(moment(startDate).startOf('month'), 'months');
      if (months % recurrence.repeatOcurrency !== 0) {
        return false;
      }

      // Right day?
      if (recurrence.repeatBy === 'dayOfTheMonth') {
        return moment(day).date() === startDate.date();
      } else {
        const recurrentWeekDay = startDate.isoWeekday();
        const recurrentNumberOfDays = Math.floor((startDate.date() - 1) / 7);

        const dayObj = moment(day);
        const dayObjWeekDay = dayObj.isoWeekday();
        const dayObjNumberOfDays = Math.floor((dayObj.date() - 1) / 7);

        return recurrentWeekDay === dayObjWeekDay &&
          recurrentNumberOfDays === dayObjNumberOfDays;
      }
    }

    return false;
  },

  // Update a current job
  changeDateModuleWithDates(teamId, jobInfo, newStartDate, newEndDate) {
    const newDateModule = FunctionUtils.cloneOrDefault(jobInfo.modules.date);
    newDateModule.startDate = newStartDate;
    newDateModule.endDate = newEndDate;
    FunctionUtils.changeDateModule(teamId, jobInfo, newDateModule);
  },
  changeDateModule(teamId, jobInfo, newDateModule) {
    const changeDateModuleFunction = (deletedDateStrs) => {
      const firebaseUpdates = {};

      // Change the module
      firebaseUpdates[`events/${jobInfo.id}/commonInfo/modules/date/`] = newDateModule;

      // If it is a temporal job then just change the module info
      if (!jobInfo.temporal) {
        const prevStartDate = jobInfo.modules.date.startDate;
        const prevEndDate = jobInfo.modules.date.endDate;

        if (jobInfo.modules.workers.list) {
          for (const workerId in jobInfo.modules.workers.list) {
            const calendarId = `${workerId}@${teamId}`;

            // Remove old instances
            let cursor = moment(prevStartDate);
            while (cursor.isSameOrBefore(prevEndDate)) {
              const dateStr = cursor.format('YYYY-MM-DD');
              if (!jobInfo.modules.date.recurrence ||
                FunctionUtils.isInsideRecurrence(dateStr, jobInfo.modules.date)) {
                const instancePath = `calendars/${calendarId}/instances/${dateStr}/${jobInfo.id}`;
                firebaseUpdates[instancePath] = null;
              }
              cursor.add(1, 'days');
            }

            // Create new instances
            cursor = moment(newDateModule.startDate);
            while (cursor.isSameOrBefore(newDateModule.endDate)) {
              const dateStr = cursor.format('YYYY-MM-DD');
              if (!deletedDateStrs.hasOwnProperty(dateStr)) {
                if (!jobInfo.modules.date.recurrence ||
                  FunctionUtils.isInsideRecurrence(dateStr, jobInfo.modules.date)) {
                  const instancePath = `calendars/${calendarId}/instances/${dateStr}/${jobInfo.id}`;
                  firebaseUpdates[instancePath] = {
                    dateStr: dateStr,
                    eventId: jobInfo.id,
                    calendarId: calendarId
                  };
                }
              }
              cursor.add(1, 'days');
            }
          }
        }
      }
      firebase.database().ref().update(firebaseUpdates);
    }

    if (jobInfo.modules.date.recurrence) {
      firebase.database()
        .ref(`events/${jobInfo.id}/extendedInfo/modules/date/deletedInstances/`)
        .once('value', (snapshot) => {
          const val = snapshot.val();
          changeDateModuleFunction(val ? val : {})
        })
    } else {
      changeDateModuleFunction({});
    }
  },
  deleteInstance(teamId, jobInfo, dateStr) {
    const firebaseUpdates = {};

    // Flag this date
    firebaseUpdates[`events/${jobInfo.id}/extendedInfo/modules/date/deletedInstances/${dateStr}`] = true;

    // Remove instances
    if (jobInfo.modules.workers.list) {
      for (const workerId in jobInfo.modules.workers.list) {
        const calendarId = `${workerId}@${teamId}`;
        const instancePath = `calendars/${calendarId}/instances/${dateStr}/${jobInfo.id}`;
        firebaseUpdates[instancePath] = null;
      }
    }

    firebase.database().ref().update(firebaseUpdates);
  }
};

export default FunctionUtils



// WEBPACK FOOTER //
// ./src/lambda/FunctionUtils.js